import { Textarea } from "@nextui-org/react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";

const DEBOUNCE_DELAY = 300;

type Props = {
  fieldValueObjArray: string[];
  onChange?: (value: string[]) => void;
  noBorder?: boolean;
  expandedView?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
};

const AgentNumberedTextfieldsListWithSource = ({
  fieldValueObjArray,
  onChange,
  noBorder,
  expandedView,
  isDisabled,
  placeholder,
}: Props) => {
  const [localValues, setLocalValues] = useState<string[]>([]);
  const isFirstRender = useRef(true);
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  // Create stable debounced function
  const debouncedOnChange = useCallback(
    debounce((values: string[]) => {
      // Filter out empty values when reporting to parent
      const filteredValues = values.filter(v => v.trim() !== "");
      onChange?.(filteredValues);
    }, DEBOUNCE_DELAY),
    [onChange]
  );

  // Initialize local values with always having an empty field at the end
  useEffect(() => {
    if (isFirstRender.current) {
      const initialValues = [...fieldValueObjArray];
      // Always ensure there's an empty field at the end for new input
      if (initialValues.length === 0 || initialValues[initialValues.length - 1] !== "") {
        initialValues.push("");
      }
      setLocalValues(initialValues);
      isFirstRender.current = false;
    }
  }, [fieldValueObjArray]);

  // Update local values when fieldValueObjArray changes externally (but not on first render)
  useEffect(() => {
    if (!isFirstRender.current) {
      const newValues = [...fieldValueObjArray];
      // Always ensure there's an empty field at the end for new input
      if (newValues.length === 0 || newValues[newValues.length - 1] !== "") {
        newValues.push("");
      }
      setLocalValues(newValues);
    }
  }, [fieldValueObjArray]);

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const handleFieldChange = useCallback(
    (newValue: string, index: number) => {
      setLocalValues(currentValues => {
        const newArray = [...currentValues];

        const isEffectivelyEmpty = newValue.trim() === '';

        if (isEffectivelyEmpty) {
          // If the value is empty, remove this field only if it's not the last field
          if (index !== newArray.length - 1) {
            newArray.splice(index, 1);
          } else {
            // If it's the last field, just keep it as empty
            newArray[index] = '';
          }
        } else {
          // Update the field value
          newArray[index] = newValue;

          // If this is the last field and now has content (not just whitespace), add a new empty field
          if (index === newArray.length - 1 && newValue.trim() !== '') {
            newArray.push('');
          }
        }

        // Call debounced onChange (it will filter out empty values)
        debouncedOnChange(newArray);

        return newArray;
      });
    },
    [debouncedOnChange]
  );

  return (
    <ol className="relative flex flex-col">
      {localValues.map((fieldValue, index) => {
        const isLastField = index === localValues.length - 1;
        const isEmpty = fieldValue.trim() === "";

        // Don't render the last empty field if component is disabled
        if (isDisabled && isLastField && isEmpty) {
          return null;
        }

        return (
          <li
            key={index}
            className={clsx(
              "bg-white flex gap-2 font-light px-4 py-2 text-small items-start",
              isLastField && isEmpty && "opacity-70" // Show opacity for empty last field
            )}
          >
            <span className="w-[18px] flex-shrink-0 pt-0 text-left">
              {index + 1}.
            </span>
            <div className="relative w-full !flex !items-start">
              <Textarea
                ref={el => {
                  if (el) {
                    const textarea = el.querySelector("textarea");
                    textareaRefs.current[index] = textarea;
                  }
                }}
                value={fieldValue}
                onChange={e => handleFieldChange(e.target.value, index)}
                className="text-[#121212] font-light bg-white focus-within:text-[#000000] h-fit"
                classNames={{
                  base: "shadow-none !border-none rounded-none truncate",
                  inputWrapper: clsx(
                    "p-0 rounded-md border-[1px] !overflow-visible !flex !items-start",
                    noBorder && "border-none"
                  ),
                  input: "!overflow-visible resize-none",
                }}
                variant="bordered"
                maxRows={expandedView ? 999999 : 999999}
                minRows={1}
                placeholder={
                  isLastField
                    ? (placeholder || "Add new item...")
                    : (placeholder || "Enter text...")
                }
                isReadOnly={isDisabled}
                disableAutosize={false}
                onMouseDown={e => {
                  const textarea = e.target as HTMLTextAreaElement;
                  if (e.target === textarea) {
                    const clickPosition = textarea.selectionStart;
                    textarea.focus({ preventScroll: true });
                    textarea.setSelectionRange(clickPosition, clickPosition);
                  }
                }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default AgentNumberedTextfieldsListWithSource;
